import "./DocumentList.css";

import { PaperclipIcon } from "../../../../assets";
import { Button } from "../../Button";
import { DocumentListItem, DocumentListVariant } from "../DocumentListItem";

export interface DocumentListDocument {
  documentUuid: string;
  fileName: string;
  documentType?: string;
  fileSize?: string;
  uploadedAt?: string;
  url?: string;
}

export interface DocumentListProps {
  documents: DocumentListDocument[];
  /** Corner treatment — `seed` (rounded, default) or `hub` (square). */
  variant?: DocumentListVariant;
  /** Heading shown above the list; hidden when readOnly. Defaults to "Attachments". */
  title?: string;
  /** Optional guidance copy rendered under the title; hidden when readOnly. */
  subTitle?: string;
  /**
   * Read-only mode: hides the heading + "Add attachment" button and removes the
   * per-row remove action, leaving a download-only list.
   */
  readOnly?: boolean;
  /** Label for the add button. Defaults to "Add attachment". */
  addButtonText?: string;
  /** Fired when the add button is pressed (e.g. open a file picker or upload overlay). */
  onAddAttachment?: () => void;
  onDownloadDocument?: (documentUuid: string) => void;
  onRemoveDocument?: (documentUuid: string) => void;
  /** Copy shown when there are no documents. */
  emptyText?: string;
}

export const DocumentList = ({
  documents,
  variant = "seed",
  title = "Attachments",
  subTitle,
  readOnly = false,
  addButtonText = "Add attachment",
  onAddAttachment,
  onDownloadDocument,
  onRemoveDocument,
  emptyText,
}: DocumentListProps): JSX.Element => {
  return (
    <div className={`DocumentList DocumentList_${variant}`}>
      {!readOnly && (
        <>
          <div className="DocumentList_header">
            <h3 className="DocumentList_title">{title}</h3>
            {subTitle && <p className="body1 DocumentList_subTitle">{subTitle}</p>}
          </div>
          {onAddAttachment && (
            <Button
              variant="secondary"
              corners={variant === "seed" ? "rounded" : "default"}
              size="small"
              minWidth={false}
              hasNoPadding
              text={addButtonText}
              icon={<PaperclipIcon />}
              iconPosition="left"
              onClick={onAddAttachment}
            />
          )}
        </>
      )}
      {documents.length > 0 ? (
        <div className="DocumentList_items">
          {documents.map((document) => (
            <DocumentListItem
              key={document.documentUuid}
              documentUuid={document.documentUuid}
              fileName={document.fileName}
              documentType={document.documentType}
              fileSize={document.fileSize}
              uploadedAt={document.uploadedAt}
              url={document.url}
              variant={variant}
              readOnly={readOnly}
              onDownload={onDownloadDocument}
              onRemove={onRemoveDocument}
            />
          ))}
        </div>
      ) : (
        emptyText && <p className="body2 DocumentList_empty">{emptyText}</p>
      )}
    </div>
  );
};
